import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerHeroSlides = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [slides, setSlides] = useState([]);
  const [editId, setEditId] = useState(0);
  const [edit, setEdit] = useState(null);
  const [nextSort, setNextSort] = useState(1);
  const [bgBase64, setBgBase64] = useState('');
  const [previewSrc, setPreviewSrc] = useState('');

  const [form, setForm] = useState({
    id: 0,
    title: '',
    subtitle: '',
    link_url: '',
    sort_order: 1,
    visible: true,
    keep_image: '',
  });

  const defaultPreview =
    'https://images.unsplash.com/photo-1606813908387-2c1a3b32f6f8?q=80&w=2070';

  const fetchData = async (currentEditId = 0) => {
    try {
      const url =
        currentEditId > 0
          ? `/api/seller/hero/slides-data?edit_id=${encodeURIComponent(currentEditId)}`
          : '/api/seller/hero/slides-data';

      const res = await axios.get(url, {
        withCredentials: true,
      });

      if (res.data && res.data.ok) {
        setShopName(res.data.shop_name || '');
        setSlides(Array.isArray(res.data.slides) ? res.data.slides : []);
        setEditId(Number(res.data.edit_id || 0));
        setEdit(res.data.edit || null);
        setNextSort(Number(res.data.next_sort || 1));
      } else {
        alert(res.data?.error || '슬라이드 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;

      if (redirect) {
        window.location.href = redirect;
        return;
      }

      alert(error || '슬라이드 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const currentEditId = Number(params.get('edit_id') || 0);
    fetchData(currentEditId);
  }, []);
 
  useEffect(() => {
    if (edit) {
      setForm({
        id: Number(edit.id || 0),
        title: edit.title || '',
        subtitle: edit.subtitle || '',
        link_url: edit.link_url || '',
        sort_order: Number(edit.sort_order || 0),
        visible: Number(edit.visible || 0) === 1,
        keep_image: edit.bg_image || '',
      });

      if (edit.bg_image) {
        setPreviewSrc(`/api/static/${String(edit.bg_image).replace(/^\/+/, '')}`);
      } else {
        setPreviewSrc(defaultPreview);
      }
    } else {
      setForm({
        id: 0,
        title: '',
        subtitle: '',
        link_url: '',
        sort_order: nextSort,
        visible: true,
        keep_image: '',
      });
      setPreviewSrc(defaultPreview);
    }

    setBgBase64('');
  }, [edit, nextSort]);

  const titleText = useMemo(() => {
    return edit ? '슬라이드 수정' : '슬라이드 추가';
  }, [edit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const fileToResizedBase64 = (file, maxW = 1920, quality = 0.82) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        try {
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        } catch (e) {
          reject(e);
        }
      };

      img.onerror = reject;
    });
  };

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await fileToResizedBase64(file, 1920, 0.82);
      setBgBase64(base64);
      setPreviewSrc(base64);
    } catch (err) {
      alert('이미지 처리 중 오류가 발생했습니다.');
    }
  };

  const handleCreateNew = () => {
    window.location.href = '/seller/hero/slides';
  };

  const handleEdit = (id) => {
    window.location.href = `/seller/hero/slides?edit_id=${id}`;
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('삭제할까요?');
    if (!ok) return;

    try {
      const formData = new FormData();
      formData.append('id', id);

      const res = await axios.post('/api/seller/hero/slide-delete', formData, {
        withCredentials: true,
      });

      if (res.data?.ok) {
        window.location.href = '/seller/hero/slides';
        return;
      }

      if (res.data?.redirect) {
        window.location.href = res.data.redirect;
        return;
      }

      alert(res.data?.error || '삭제 중 오류가 발생했습니다.');
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;

      if (redirect) {
        window.location.href = redirect;
        return;
      }

      alert(error || '삭제 중 오류가 발생했습니다.');
    }
  };

  const handleSort = async (dir, id) => {
    try {
      const formData = new FormData();
      formData.append('id', id);
      formData.append('dir', dir);

      const res = await axios.post('/api/seller/hero/slide-move', formData, {
        withCredentials: true,
      });

      if (res.data?.ok) {
        window.location.href = '/seller/hero/slides';
        return;
      }

      if (res.data?.redirect) {
        window.location.href = res.data.redirect;
        return;
      }

      alert(res.data?.error || '정렬 변경 중 오류가 발생했습니다.');
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;

      if (redirect) {
        window.location.href = redirect;
        return;
      }

      alert(error || '정렬 변경 중 오류가 발생했습니다.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    formData.append('id', form.id);
    formData.append('bg_base64', bgBase64);
    formData.append('title', form.title);
    formData.append('subtitle', form.subtitle);
    formData.append('link_url', form.link_url);
    formData.append('sort_order', form.sort_order);
    if (form.visible) {
      formData.append('visible', '1');
    }
    if (form.keep_image) {
      formData.append('keep_image', form.keep_image);
    }

    try {
      const res = await axios.post('/api/seller/hero/slide-save', formData, {
        withCredentials: true,
      });

      if (res.data?.ok) {
        window.location.href = '/seller/hero/slides';
        return;
      }

      if (res.data?.redirect) {
        window.location.href = res.data.redirect;
        return;
      }

      alert(res.data?.error || '저장 중 오류가 발생했습니다.');
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;

      if (redirect) {
        window.location.href = redirect;
        return;
      }

      alert(error || '저장 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loadingWrap}>로딩중...</div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.topBar}>
        <h4 style={styles.topBarTitle}>메인 배너 슬라이드 관리</h4>
      </div>

      <div style={styles.row}>
        <div style={styles.leftCol}>
          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={styles.cardHead}>
                <h6 style={styles.cardHeadTitle}>슬라이드 목록 ({slides.length})</h6>
                <button type="button" style={styles.primaryBtnSm} onClick={handleCreateNew}>
                  새 슬라이드
                </button>
              </div>

              {slides.length === 0 ? (
                <div style={styles.warningAlert}>아직 등록된 슬라이드가 없습니다.</div>
              ) : (
                <div style={styles.tableResponsive}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>이미지</th>
                        <th style={styles.th}>문구</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>정렬</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>표시</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slides.map((s) => (
                        <tr key={s.id}>
                          <td style={styles.td}>
                            <img
                              style={styles.thumb}
                              src={`/api/static/${String(s.bg_image || '').replace(/^\/+/, '')}`}
                              alt=""
                            />
                          </td>
                          <td style={styles.tdLeft}>
                            <div style={styles.slideTitle}>{s.title}</div>
                            <div style={styles.slideSubtitle}>{s.subtitle || ''}</div>
                            {s.link_url ? (
                              <div style={styles.mono}>{s.link_url}</div>
                            ) : null}
                          </td>
                          <td style={styles.tdCenter}>
                            <div style={styles.smallMuted}>#{Number(s.sort_order || 0)}</div>
                            <div style={styles.btnGroupSm}>
                              <button
                                type="button"
                                style={styles.outlineBtnSm}
                                onClick={() => handleSort('up', s.id)}
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                style={styles.outlineBtnSm}
                                onClick={() => handleSort('down', s.id)}
                              >
                                ▼
                              </button>
                            </div>
                          </td>
                          <td style={styles.tdCenter}>
                            {Number(s.visible || 0) === 1 ? (
                              <span style={styles.badgeSuccess}>ON</span>
                            ) : (
                              <span style={styles.badgeSecondary}>OFF</span>
                            )}
                          </td>
                          <td style={styles.tdRight}>
                            <button
                              type="button"
                              style={styles.outlinePrimaryBtnSm}
                              onClick={() => handleEdit(s.id)}
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              style={styles.outlineDangerBtnSm}
                              onClick={() => handleDelete(s.id)}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.rightCol}>
          <div style={styles.card}>
            <div style={styles.cardBody}>
              <h6 style={styles.formTitle}>{titleText}</h6>

              <form onSubmit={handleSubmit}>
                <input type="hidden" name="id" value={form.id} />
                <input type="hidden" name="bg_base64" value={bgBase64} />

                <div style={styles.mb3}>
                  <label style={styles.label}>제목</label>
                  <input
                    type="text"
                    name="title"
                    maxLength="100"
                    required
                    value={form.title}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>

                <div style={styles.mb3}>
                  <label style={styles.label}>설명</label>
                  <input
                    type="text"
                    name="subtitle"
                    maxLength="255"
                    value={form.subtitle}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>

                <div style={styles.mb3}>
                  <label style={styles.label}>링크(선택)</label>
                  <input
                    type="url"
                    name="link_url"
                    maxLength="255"
                    value={form.link_url}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>

                <div style={styles.rowG2}>
                  <div style={styles.col6}>
                    <label style={styles.label}>정렬값</label>
                    <input
                      type="number"
                      name="sort_order"
                      value={form.sort_order}
                      onChange={handleChange}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.col6End}>
                    <label style={styles.switchWrap}>
                      <input
                        type="checkbox"
                        name="visible"
                        checked={form.visible}
                        onChange={handleChange}
                      />
                      <span style={styles.switchLabel}>표시(ON)</span>
                    </label>
                  </div>
                </div>

                <hr style={styles.hr} />

                <div style={styles.mb2}>
                  <label style={styles.label}>배경 이미지 (기본 리사이즈 적용)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBgFileChange}
                    style={styles.input}
                  />
                  <div style={styles.formText}>
                    자동 리사이즈: 최대 가로 1920 / WebP 우선 / 품질 0.82
                  </div>
                </div>

                <div style={styles.mb3}>
                  <img style={styles.preview} src={previewSrc || defaultPreview} alt="" />
                </div>

                <button type="submit" style={styles.primaryBtnBlock}>
                  {edit ? '수정 저장' : '추가 저장'}
                </button>
              </form>

              <div style={styles.helpText}>
                ※ 이미지를 선택하지 않으면(수정 시) 기존 이미지가 유지됩니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </SellerLayout>
  );
};

const styles = {
  loadingWrap: {
    padding: '40px 0',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  topBarTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
  },
  row: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  leftCol: {
    flex: '1 1 60%',
    minWidth: '320px',
  },
  rightCol: {
    flex: '1 1 36%',
    minWidth: '320px',
  },
  card: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.125)',
    borderRadius: '10px',
    boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
  },
  cardBody: {
    padding: '16px',
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '12px',
  },
  cardHeadTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
  },
  formTitle: {
    marginTop: 0,
    marginBottom: '16px',
    fontSize: '16px',
    fontWeight: 600,
  },
  tableResponsive: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: '#f8f9fa',
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    textAlign: 'left',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'middle',
  },
  tdLeft: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'middle',
    textAlign: 'left',
  },
  tdCenter: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'middle',
    textAlign: 'center',
  },
  tdRight: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'middle',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  thumb: {
    width: '160px',
    height: '90px',
    objectFit: 'cover',
    borderRadius: '10px',
    border: '1px solid rgba(0,0,0,.12)',
  },
  preview: {
    width: '100%',
    maxHeight: '240px',
    objectFit: 'cover',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,.12)',
  },
  slideTitle: {
    fontWeight: 600,
  },
  slideSubtitle: {
    color: '#6c757d',
    fontSize: '13px',
    marginTop: '4px',
  },
  mono: {
    marginTop: '4px',
    color: '#6c757d',
    fontSize: '12px',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    wordBreak: 'break-all',
  },
  smallMuted: {
    fontSize: '12px',
    color: '#6c757d',
    marginBottom: '6px',
  },
  btnGroupSm: {
    display: 'inline-flex',
    gap: '4px',
  },
  primaryBtnSm: {
    padding: '6px 10px',
    fontSize: '13px',
    border: 'none',
    borderRadius: '6px',
    background: '#0d6efd',
    color: '#fff',
    cursor: 'pointer',
  },
  outlineBtnSm: {
    padding: '4px 8px',
    fontSize: '12px',
    border: '1px solid #6c757d',
    borderRadius: '6px',
    background: '#fff',
    color: '#6c757d',
    cursor: 'pointer',
  },
  outlinePrimaryBtnSm: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #0d6efd',
    borderRadius: '6px',
    background: '#fff',
    color: '#0d6efd',
    cursor: 'pointer',
    marginRight: '6px',
  },
  outlineDangerBtnSm: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #dc3545',
    borderRadius: '6px',
    background: '#fff',
    color: '#dc3545',
    cursor: 'pointer',
  },
  badgeSuccess: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#198754',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
  },
  badgeSecondary: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#6c757d',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
  },
  warningAlert: {
    padding: '12px 14px',
    borderRadius: '8px',
    background: '#fff3cd',
    color: '#664d03',
    border: '1px solid #ffecb5',
    marginBottom: 0,
  },
  mb3: {
    marginBottom: '16px',
  },
  mb2: {
    marginBottom: '8px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 600,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    background: '#fff',
  },
  rowG2: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  col6: {
    flex: '1 1 48%',
    minWidth: '140px',
  },
  col6End: {
    flex: '1 1 48%',
    minWidth: '140px',
    display: 'flex',
    alignItems: 'flex-end',
  },
  switchWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  switchLabel: {
    fontSize: '14px',
  },
  hr: {
    margin: '16px 0',
    border: 0,
    borderTop: '1px solid #dee2e6',
  },
  formText: {
    marginTop: '6px',
    color: '#6c757d',
    fontSize: '12px',
  },
  primaryBtnBlock: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: 'none',
    borderRadius: '6px',
    background: '#0d6efd',
    color: '#fff',
    cursor: 'pointer',
  },
  helpText: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#6c757d',
  },
};

export default SellerHeroSlides;
